import { useEffect } from 'react';
import { completeExam } from '../../api/fetchExam';
import { getTranslations } from '../../utils/appLanguage';

const normalizeLang = (lang) => {
  if (!lang) return 'en';
  if (lang === 'ua') return 'uk';
  return lang;
};

const getAssistantExamTexts = (lang) => {
  const normalizedLang = normalizeLang(lang);
  const t = getTranslations(normalizedLang);
  const fallback = getTranslations('en');

  return {
    intro: t?.assistant?.exam?.introWhileCalculating || fallback?.assistant?.exam?.introWhileCalculating || '',
    timeout: t?.assistant?.exam?.timeout || fallback?.assistant?.exam?.timeout || '',
    jokes: t?.assistant?.exam?.jokes || fallback?.assistant?.exam?.jokes || [],
  };
};

const useJokeDuringResult = ({
  state,
  currentIndex,
  questions,
  actions,
  userId,
  examId,
  dispatch,
  targetLanguage,
}) => {
  useEffect(() => {
    if (!(state.success && currentIndex + 1 >= questions.length && !state.result)) {
      return;
    }

    let jokeTimeoutId;
    let resultTimeoutId;
    let jokeIndex = 0;

    const { intro, timeout, jokes } = getAssistantExamTexts(targetLanguage);

    const tellJokeAndPoll = async () => {
      if (state.result) return;
      if (!Array.isArray(jokes) || jokes.length === 0) return;

      const joke = jokes[jokeIndex % jokes.length];
      jokeIndex += 1;

      actions.speakText(joke, { variant: 'sticky' });

      const speed = 25;
      const ms = Math.min(12000, Math.max(3500, joke.length * speed));
      await new Promise((res) => {
        jokeTimeoutId = setTimeout(res, ms);
      });

      try {
        const res = await completeExam({ userId, examId });
        if (res?.result) {
          dispatch({ type: 'SET_RESULT', payload: res });
          return;
        }
      } catch {}

      jokeTimeoutId = setTimeout(tellJokeAndPoll, 3000);
    };

    const startJokeSequence = async () => {
      try {
        if (intro) {
          actions.speakText(intro);
        }
      } catch {}

      await new Promise((res) => setTimeout(res, 6000));
      tellJokeAndPoll();
    };

    resultTimeoutId = setTimeout(() => {
      if (timeout) {
        actions.speakText(timeout);
      }
    }, 60000);

    startJokeSequence();

    return () => {
      clearTimeout(jokeTimeoutId);
      clearTimeout(resultTimeoutId);
    };
  }, [state.success, currentIndex, state.result, questions.length, actions, userId, examId, dispatch, targetLanguage]);
};

export default useJokeDuringResult;
